/** Places the six authored anchors on the generated graph (M3 spec §3). */
import { logger } from '../core/logger'
import { createRng, nextFloat, type Rng } from '../core/rng'
import type { GalaxyConfig } from '../data/galaxyConfig'
import { homeIndex, systemId, type PlacedSystem } from './placement'
import { bfsHops } from './relays'
import { deriveSeed } from './seed'
import type { AnchorId, RegionId, Relay } from './types'

/** Info message (with `{ anchor }`) when an anchor's preferred rule could not be met and a fallback chose it. */
export const ANCHOR_FALLBACK = 'galaxy: anchor placed by fallback'

function pick(rng: Rng, list: readonly number[]): number {
  return list[Math.floor(nextFloat(rng) * list.length)]
}

/** Lexicographic comparison of two sort keys. */
function lexLess(a: readonly number[], b: readonly number[]): boolean {
  for (let k = 0; k < a.length; k++) if (a[k] !== b[k]) return a[k] < b[k]
  return false
}

/** Hop count with unreachable (-1) treated as infinitely far. */
function far(h: number): number {
  return h < 0 ? Infinity : h
}

/**
 * Rules: home-gate is the gate system nearest the origin; authority-seat is another gate system one hop from
 * home when possible; megacorp-hq is a mid system with degree ≥ 3; descendant-capital is deep; guardian-base is
 * deep and not adjacent to the capital; okafor-hideout is deep or rim and ≥ 2 hops from the guardian base.
 * "One hop" and "degree" count regular relays only; the separation rules count every relay, long ones included.
 * Each anchor's `onlineBias` (D-037) then leans the pick towards systems reachable online from home (p > 0) or
 * unreachable ones (p < 0), so the megacorp HQ sits in the working network and the guardian base mostly doesn't.
 */
export function assignAnchors(
  seed: number,
  placed: readonly PlacedSystem[],
  relays: readonly Relay[],
  cfg: GalaxyConfig,
): (AnchorId | null)[] {
  const n = placed.length
  const rng = createRng(deriveSeed(seed, 'anchors'))
  const index = new Map(placed.map((_, i) => [systemId(i), i]))
  const idx = (id: string): number => {
    const i = index.get(id)
    if (i === undefined) throw new Error(`assignAnchors: relay names unknown system ${id}`)
    return i
  }
  const all = relays.map((r): [number, number] => [idx(r.a), idx(r.b)])
  const short = all.filter((_, i) => !relays[i].long)
  const degree = new Array<number>(n).fill(0)
  for (const [a, b] of short) {
    degree[a]++
    degree[b]++
  }
  const out: (AnchorId | null)[] = new Array<AnchorId | null>(n).fill(null)
  const free = (...regions: RegionId[]): number[] =>
    placed.flatMap((p, i) => (out[i] === null && regions.includes(p.region) ? [i] : []))
  /** Logs, then takes the pool member with the smallest key (first in index order on ties). */
  const fallback = (anchor: AnchorId, pool: number[], key: (i: number) => readonly number[]): number => {
    logger.info(ANCHOR_FALLBACK, { anchor })
    return pool.reduce((best, i) => (lexLess(key(i), key(best)) ? i : best))
  }

  const home = homeIndex(placed)
  out[home] = 'home-gate'
  const onlineHops = bfsHops(n, all.filter((_, i) => relays[i].online), home)
  /** A seeded pick from `pool`, leaning on the anchor's onlineBias. Always draws the bias roll, for stable sequences. */
  const choose = (anchor: AnchorId, pool: readonly number[]): number => {
    const bias = cfg.anchors[anchor].onlineBias ?? 0
    const roll = nextFloat(rng)
    const lean =
      bias > 0 && roll < bias ? pool.filter((i) => onlineHops[i] >= 0) : bias < 0 && roll < -bias ? pool.filter((i) => onlineHops[i] < 0) : []
    return pick(rng, lean.length > 0 ? lean : pool)
  }

  const homeHops = bfsHops(n, short, home)
  const gate = free('gate')
  const nextToHome = gate.filter((i) => homeHops[i] === 1)
  const dist = (i: number): number => Math.hypot(placed[i].mapPos[0] - placed[home].mapPos[0], placed[i].mapPos[1] - placed[home].mapPos[1])
  out[nextToHome.length > 0 ? choose('authority-seat', nextToHome) : fallback('authority-seat', gate, (i) => [far(homeHops[i]), dist(i)])] =
    'authority-seat'

  const mid = free('mid')
  const hubs = mid.filter((i) => degree[i] >= 3)
  out[hubs.length > 0 ? choose('megacorp-hq', hubs) : fallback('megacorp-hq', mid, (i) => [-degree[i]])] = 'megacorp-hq'

  const capital = choose('descendant-capital', free('deep'))
  out[capital] = 'descendant-capital'

  const capitalHops = bfsHops(n, all, capital)
  const deep = free('deep')
  const apart = deep.filter((i) => far(capitalHops[i]) >= 2)
  const guardian = apart.length > 0 ? choose('guardian-base', apart) : fallback('guardian-base', deep, (i) => [-far(capitalHops[i])])
  out[guardian] = 'guardian-base'

  const guardianHops = bfsHops(n, all, guardian)
  const hideouts = free('deep', 'rim')
  const hidden = hideouts.filter((i) => far(guardianHops[i]) >= 2)
  out[hidden.length > 0 ? choose('okafor-hideout', hidden) : fallback('okafor-hideout', hideouts, (i) => [-far(guardianHops[i])])] = 'okafor-hideout'

  return out
}
